import React from 'react'
import { Tag } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { useClipboard } from '@chakra-ui/react'
import { Text, Flex, Spacer } from '@chakra-ui/react'
import { FaBell, FaMobileButton, FaPhone } from 'react-icons/fa6'

import {
    ClientPhoneFieldsFragment,
    useMakeClientPhonePrimaryMutation,
} from '~graphql/generated/graphql'
import { getPhoneCountryFromCode } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import SharedStack from '~components/shared/wrappers/shared-stack'
import IconWithTooltip from '~components/shared/icons/icon-with-tooltip'
import { useClinic } from '~contexts/clinic-context'

interface ClientPhoneItemProps {
    phone: ClientPhoneFieldsFragment
    isPrimary?: boolean
    clinicId: string
    clientId: string
    country?: string
    refetchQueries?: string[]
}

export default function ClientPhoneItem({
    phone,
    isPrimary,
    clinicId,
    clientId,
    country,
    refetchQueries,
}: ClientPhoneItemProps) {
    const { clinic } = useClinic()
    const phoneIcon = phone.is_mobile ? FaMobileButton : FaPhone

    const phoneCountry =
        phone?.country_code
            ? getPhoneCountryFromCode(phone.country_code ?? '')
            : country || clinic?.address?.country

    const phoneFormatted = formatPhoneReadable(phone.phone, phoneCountry)

    const { onCopy, hasCopied } = useClipboard(phoneFormatted || '')

    const [makePhonePrimary, { loading }] = useMakeClientPhonePrimaryMutation({
        refetchQueries: refetchQueries || [],
        awaitRefetchQueries: true,
    })

    async function handleMakePrimary() {
        await makePhonePrimary({
            variables: {
                clinicId,
                clientId,
                phoneId: phone.id,
            },
        })
    }

    return (
        <Flex
            w="full"
            flexDir="row"
            alignItems="center"
            px={2}
            py={2}
            rounded="lg"
            cursor="pointer"
            _hover={{
                bg: 'gray.50',
            }}
            _active={{
                bg: 'gray.100',
            }}
            transition="0.1s"
            role="group"
        >
            <SharedStack row fit align="flex-start" spacing={1}>
                <IconWithTooltip
                    icon={phoneIcon}
                    label={phone.is_mobile ? 'Mobile Phone' : 'Home Phone'}
                    fontSize="13px"
                    color={phone.is_active ? 'gray.800' : 'gray.400'}
                    centerOverrides={{
                        h: '18px',
                        w: '18px',
                    }}
                />
                <SharedStack w="max-content" gap={0}>
                    <Text
                        fontWeight="medium"
                        fontSize="xs"
                        color={phone.is_active ? 'gray.800' : 'gray.400'}
                    >
                        {phoneFormatted}
                    </Text>
                    {phone.name && (
                        <Text
                            fontSize="10px"
                            color={phone.is_active ? 'gray.600' : 'gray.400'}
                            maxW="120px"
                        >
                            {phone.name}
                        </Text>
                    )}
                </SharedStack>
                {isPrimary && (
                    <IconWithTooltip
                        label="Primary"
                        icon={FaBell}
                        color="brand.600"
                        fontSize="13px"
                        centerOverrides={{
                            h: '18px',
                            w: '18px',
                        }}
                    />
                )}
                {!phone.is_active && (
                    <Tag ml={1} size="xs" colorScheme="gray">
                        Inactive
                    </Tag>
                )}
            </SharedStack>
            <Spacer />
            <SharedStack
                row
                fit
                gap={1}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                transition="0.1s"
            >
                <Button
                    size="xs"
                    variant="subtle"
                    onClick={onCopy}
                    isDisabled={hasCopied}
                >
                    {hasCopied ? 'Copied!' : 'Copy'}
                </Button>
                {!isPrimary && (
                    <Button
                        size="xs"
                        variant="subtle"
                        colorScheme="brand"
                        loadingText="Setting..."
                        isLoading={loading}
                        onClick={handleMakePrimary}
                    >
                        Set Primary
                    </Button>
                )}
            </SharedStack>
        </Flex>
    )
}
